import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from 'react-router-dom';

import { ROUTES } from '@core';

import { ErrorBoundary } from './error-boundary';
import Loader from './loader.tsx';
import NativeFeatureGuard from './native-feature.guard.tsx';
import { retryDynamicImport } from './retry-dynamic-import';

const Main = Loader(
  retryDynamicImport(() => import('../../routes/main/main.tsx'))
);
const AppShell = Loader(
  retryDynamicImport(() => import('./app-shell/app-shell.tsx'))
);
const DeepLink = Loader(
  retryDynamicImport(() => import('../../routes/deep-link/deep-link.tsx'))
);
const SiteConsents = Loader(
  retryDynamicImport(
    () => import('../../routes/site-consents/site-consents.tsx')
  )
);
const PolicyUpdate = Loader(
  retryDynamicImport(
    () => import('../../routes/policy-update/policy-update.tsx')
  )
);
const Intro = Loader(
  retryDynamicImport(() => import('../../routes/auth/intro/intro.tsx')),
  'authentication'
);
const AuthLayout = Loader(
  retryDynamicImport(() => import('../../routes/auth/auth-layout.tsx'))
);
const LoginLayout = Loader(
  retryDynamicImport(
    () => import('../../routes/auth/login/login-layout/login-layout.tsx')
  )
);
const Login = Loader(
  retryDynamicImport(
    () => import('../../routes/auth/login/login-form/login.tsx')
  ),
  'login'
);

const TwoFactorAuth = Loader(
  retryDynamicImport(
    () => import('../../routes/auth/login/two-factor-auth/two-factor-auth.tsx')
  ),
  'pin'
);
const EmailVerify = Loader(
  retryDynamicImport(
    () => import('../../routes/auth/login/email-verify/email-verify.tsx')
  ),
  'email verify'
);
const AccountLoader = Loader(
  retryDynamicImport(
    () => import('../../routes/auth/login/account-loader/account-loader.tsx')
  ),
  'account loader'
);
const InstanceConnection = Loader(
  retryDynamicImport(
    () =>
      import(
        '../../routes/auth/login/instance-connection/instance-connection.tsx'
      )
  ),
  'instance connection'
);
const PrivateRoutes = Loader(
  retryDynamicImport(() => import('./private-routes/private-routes.tsx'))
);
const ForgotPassword = Loader(
  retryDynamicImport(
    () => import('../../routes/auth/login/forgot-password/forgot-password.tsx')
  ),
  'forgot password'
);

// Registration
const RegisterLayout = Loader(
  retryDynamicImport(
    () =>
      import('../../routes/auth/register/register-layout/register-layout.tsx')
  )
);
const RegisterConsents = Loader(
  retryDynamicImport(
    () =>
      import('../../routes/auth/register/register-policy/register-consents.tsx')
  )
);
const RegisterQRScan = Loader(
  retryDynamicImport(
    () =>
      import('../../routes/auth/register/register-qr-scan/register-qr-scan.tsx')
  )
);
const RegisterEmail = Loader(
  retryDynamicImport(
    () => import('../../routes/auth/register/register-email/register-email.tsx')
  )
);
const RegisterBirthday = Loader(
  retryDynamicImport(
    () =>
      import(
        '../../routes/auth/register/register-birthday/register-birthday.tsx'
      )
  )
);
const RegisterName = Loader(
  retryDynamicImport(
    () => import('../../routes/auth/register/register-name/register-name.tsx')
  )
);
const RegisterGender = Loader(
  retryDynamicImport(
    () =>
      import('../../routes/auth/register/register-gender/register-gender.tsx')
  )
);
const RegisterPassword = Loader(
  retryDynamicImport(
    () =>
      import(
        '../../routes/auth/register/register-password/register-password.tsx'
      )
  )
);
const RegisterVerify = Loader(
  retryDynamicImport(
    () =>
      import('../../routes/auth/register/register-verify/register-verify.tsx')
  )
);
const RegisterAuthenticate = Loader(
  retryDynamicImport(
    () =>
      import(
        '../../routes/auth/register/register-authenticate/register-authenticate.tsx'
      )
  )
);
const RegisterConfirmProfile = Loader(
  retryDynamicImport(
    () =>
      import(
        '../../routes/auth/register/register-confirm-profile/register-confirm-profile.tsx'
      )
  )
);

const DashboardLayout = Loader(
  retryDynamicImport(
    () => import('../../routes/dashboard/dashboard-layout.tsx')
  )
);
const Home = Loader(
  retryDynamicImport(() => import('../../routes/dashboard/home/home.tsx')),
  'home'
);

const Medication = Loader(
  retryDynamicImport(
    () => import('../../routes/dashboard/medication/medication.tsx')
  ),
  'medication'
);
const MedicationPlan = Loader(
  retryDynamicImport(
    () =>
      import(
        '../../routes/dashboard/medication/medication-plan/medication-plan.tsx'
      )
  ),
  'medication plan'
);
const Appointment = Loader(
  retryDynamicImport(
    () => import('../../routes/dashboard/appointment/appointment.tsx')
  ),
  'appointment'
);
const Stats = Loader(
  retryDynamicImport(() => import('../../routes/dashboard/stats/stats.tsx')),
  'stats'
);
const Profile = Loader(
  retryDynamicImport(
    () => import('../../routes/dashboard/settings/profile/profile.tsx')
  ),
  'profile'
);
const Settings = Loader(
  retryDynamicImport(
    () => import('../../routes/dashboard/settings/settings.tsx')
  ),
  'settings'
);
const Security = Loader(
  retryDynamicImport(
    () => import('../../routes/dashboard/settings/security/security.tsx')
  ),
  'security'
);
const LanguageChooser = Loader(
  retryDynamicImport(
    () => import('../../routes/dashboard/settings/language-chooser.tsx')
  ),
  'languages'
);
const Imprint = Loader(
  retryDynamicImport(
    () => import('../../routes/dashboard/settings/imprint/imprint.tsx')
  ),
  'imprint'
);
const Notifications = Loader(
  retryDynamicImport(
    () =>
      import('../../routes/dashboard/settings/notifications/notifications.tsx')
  ),
  'notification settings'
);
const HealthData = Loader(
  retryDynamicImport(
    () =>
      import(
        '../../routes/dashboard/settings/consents/health-data/health-data.tsx'
      )
  ),
  'consents health data'
);
const DeleteAccount = Loader(
  retryDynamicImport(
    () =>
      import(
        '../../routes/dashboard/settings/consents/delete-account/delete-account.tsx'
      )
  ),
  'delete account'
);
const HealthSettings = Loader(
  retryDynamicImport(
    () =>
      import(
        '../../routes/dashboard/settings/consents/health-data/health-data-settings.tsx'
      )
  ),
  'consents health data'
);

const ConsentMyOnCare = Loader(
  retryDynamicImport(
    () =>
      import('../../routes/dashboard/settings/consents/myoncare/myoncare.tsx')
  ),
  'consents my on care'
);
const Answering = Loader(
  retryDynamicImport(
    () => import('../../routes/dashboard/home/answering/answering.tsx')
  ),
  'task answering'
);

const SecurityPin = Loader(
  retryDynamicImport(() => import('../../routes/security/pin/pin.tsx')),
  'security pin'
);

const DeactivatePin = Loader(
  retryDynamicImport(
    () => import('../../routes/security/deactivate-pin/deactivate-pin.tsx')
  ),
  'deactivate security pin'
);

const Chat = Loader(
  retryDynamicImport(() => import('../../routes/chat/chat.tsx')),
  'chat'
);
const Files = Loader(
  retryDynamicImport(() => import('../../routes/dashboard/files/files.tsx')),
  'files'
);
const FilePreview = Loader(
  retryDynamicImport(
    () => import('../../routes/dashboard/files/file-preview/file-preview.tsx')
  ),
  'File preview'
);

const CareTeam = Loader(
  retryDynamicImport(
    () => import('../../routes/dashboard/careteam/careteam.tsx')
  ),
  'careteam'
);

const CareTeamDetails = Loader(
  retryDynamicImport(
    () => import('../../routes/dashboard/careteam/careteam-details.tsx')
  ),
  'careteam details'
);

const NotFound = Loader(
  retryDynamicImport(() => import('./not-found.tsx')),
  'not found'
);

const MaintenancePage = Loader(
  retryDynamicImport(
    () => import('../../routes/maintenance-page/maintenance-page.tsx')
  ),
  'maintenance'
);

const UpdateScreen = Loader(
  retryDynamicImport(
    () => import('../../routes/update-screen/update-screen.tsx')
  ),
  'update'
);

const router = createBrowserRouter([
  {
    errorElement: <ErrorBoundary />,
    element: <AppShell />,
    children: [
      {
        path: ROUTES.main,
        element: <Main />,
      },
      {
        element: <AuthLayout />,
        children: [
          {
            element: <LoginLayout />,
            children: [
              {
                path: ROUTES.auth.intro,
                element: <Intro />,
              },
              {
                path: ROUTES.auth.login.login,
                element: <Login />,
              },
              {
                path: ROUTES.auth.login.pin,
                element: <TwoFactorAuth />,
              },
              {
                path: ROUTES.auth.login.emailVerify,
                element: <EmailVerify />,
              },
              {
                path: ROUTES.auth.login.accountLoader,
                element: <AccountLoader />,
              },
              {
                path: ROUTES.auth.login.forgotPassword,
                element: <ForgotPassword />,
              },
              {
                path: ROUTES.auth.login.instanceConnection,
                element: <InstanceConnection />,
              },
              {
                path: ROUTES.siteConsents,
                element: <SiteConsents />,
              },
              {
                path: ROUTES.policyUpdate,
                element: <PolicyUpdate />,
              },
              {
                path: ROUTES.deepLink,
                element: <DeepLink />,
              },
            ],
          },
          {
            element: <RegisterLayout />,
            children: [
              {
                path: ROUTES.auth.register.index,
                element: <RegisterQRScan />,
              },
              {
                path: ROUTES.auth.register.consents,
                element: <RegisterConsents />,
              },
              {
                path: ROUTES.auth.register.email,
                element: <RegisterEmail />,
              },
              {
                path: ROUTES.auth.register.password,
                element: <RegisterPassword />,
              },
              {
                path: ROUTES.auth.register.verify,
                element: <RegisterVerify />,
              },
              {
                path: ROUTES.auth.register.birthday,
                element: <RegisterBirthday />,
              },
              {
                path: ROUTES.auth.register.name,
                element: <RegisterName />,
              },
              {
                path: ROUTES.auth.register.gender,
                element: <RegisterGender />,
              },
              {
                path: ROUTES.auth.register.authenticate,
                element: <RegisterAuthenticate />,
              },
              {
                path: ROUTES.auth.register.confirmProfile,
                element: <RegisterConfirmProfile />,
              },
            ],
          },
        ],
      },
      {
        element: <PrivateRoutes />,
        children: [
          {
            path: ROUTES.dashboard.index,
            element: <DashboardLayout />,
            children: [
              {
                index: true,
                element: <Navigate replace to={ROUTES.dashboard.home} />,
              },
              { path: ROUTES.dashboard.home, element: <Home /> },
              { path: ROUTES.dashboard.medication, element: <Medication /> },
              { path: ROUTES.dashboard.appointment, element: <Appointment /> },
              { path: ROUTES.dashboard.stats, element: <Stats /> },
              { path: ROUTES.dashboard.settings.index, element: <Settings /> },
              { path: ROUTES.dashboard.files.index, element: <Files /> },
              { path: ROUTES.dashboard.careTeams.list, element: <CareTeam /> },
            ],
          },
          { path: ROUTES.dashboard.settings.security, element: <Security /> },
          {
            path: ROUTES.dashboard.settings.health.options,
            element: (
              <NativeFeatureGuard>
                <HealthData />
              </NativeFeatureGuard>
            ),
          },
          {
            path: ROUTES.dashboard.settings.health.settings,
            element: (
              <NativeFeatureGuard>
                <HealthSettings />
              </NativeFeatureGuard>
            ),
          },
          {
            path: ROUTES.dashboard.settings.language,
            element: <LanguageChooser />,
          },
          { path: ROUTES.dashboard.settings.imprint, element: <Imprint /> },
          {
            path: ROUTES.dashboard.settings.notifications,
            element: <Notifications />,
          },
          { path: ROUTES.dashboard.settings.profile, element: <Profile /> },
          {
            path: ROUTES.dashboard.settings.myoncare,
            element: <ConsentMyOnCare />,
          },
          {
            path: ROUTES.dashboard.settings.deleteAccount,
            element: <DeleteAccount />,
          },
          { path: ROUTES.security.pin, element: <SecurityPin /> },
          { path: ROUTES.security.deactivatePin, element: <DeactivatePin /> },
          { path: ROUTES.medicationPlan, element: <MedicationPlan /> },
          {
            path: ROUTES.dashboard.careTeams.details.index,
            element: <CareTeamDetails />,
          },
          { path: ROUTES.answering, element: <Answering /> },
          { path: ROUTES.dashboard.careTeams.details.chat, element: <Chat /> },
          { path: ROUTES.maintenancePage, element: <MaintenancePage /> },
          { path: ROUTES.update, element: <UpdateScreen /> },
          { path: ROUTES.dashboard.files.preview, element: <FilePreview /> },
        ],
      },
    ],
  },
  { path: '*', element: <NotFound /> },
]);

export const RouteProvider = () => <RouterProvider router={router} />;
